/**
 * Phase 4+ — Compliance Status Card
 * 
 * Shows the overall compliance status of a case at a glance.
 * Red/yellow/green indicators for audit readiness.
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Shield,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Lock,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CaseComplianceStatus } from '@/lib/compliance/types';
import { COMPLIANCE_DISCLAIMER, DEMO_COMPLIANCE_NOTICE } from '@/lib/compliance/types';

interface ComplianceStatusCardProps {
  compliance: CaseComplianceStatus;
  isDemoMode: boolean;
}

const STATUS_CONFIG = {
  compliant: {
    icon: ShieldCheck,
    label: 'Compliant',
    description: 'Case is audit-ready and defensible',
    color: 'text-success',
    bgColor: 'bg-success/10 border-success/30',
    badgeClass: 'bg-success text-success-foreground',
  },
  review_required: {
    icon: ShieldAlert,
    label: 'Review Required',
    description: 'Case has overrides or warnings requiring compliance review',
    color: 'text-warning',
    bgColor: 'bg-warning/10 border-warning/30',
    badgeClass: 'bg-warning text-warning-foreground',
  },
  blocked: {
    icon: ShieldX,
    label: 'Blocked',
    description: 'Case has blocking issues that must be resolved before export',
    color: 'text-destructive',
    bgColor: 'bg-destructive/10 border-destructive/30',
    badgeClass: 'bg-destructive text-destructive-foreground',
  },
};

export function ComplianceStatusCard({ compliance, isDemoMode }: ComplianceStatusCardProps) {
  const config = STATUS_CONFIG[compliance.status];
  const StatusIcon = config.icon;

  const checks = [
    { label: 'Surgeon Attestation', passed: compliance.attestationsComplete, required: true },
    { label: 'All CPTs Confirmed', passed: compliance.allCptsConfirmed, required: true },
    { label: 'Export Gates Pass', passed: compliance.exportGatesPass, required: true },
    { label: 'Audit Timeline Complete', passed: compliance.auditTimelineComplete, required: true },
    { label: 'No Conflicts', passed: !compliance.hasConflicts, required: true },
    { label: 'No Overrides', passed: !compliance.hasOverrides, required: false },
  ];

  const passedCount = checks.filter(c => c.passed).length;
  const totalRequired = checks.filter(c => c.required).length;
  const passedRequired = checks.filter(c => c.required && c.passed).length;
  const progressPercent = (passedRequired / totalRequired) * 100;

  return (
    <Card className={cn('border-2', config.bgColor)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <StatusIcon className={cn('h-5 w-5', config.color)} />
            Compliance Status
            {isDemoMode && (
              <Badge variant="outline" className="text-xs bg-warning/10 text-warning border-warning/30">
                Demo
              </Badge>
            )}
          </CardTitle>
          <Badge className={cn('text-xs', config.badgeClass)}>
            {config.label}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Compliance Checks</span>
            <span className="font-medium">{passedRequired}/{totalRequired} required</span>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </div>

        {/* Checklist */}
        <div className="space-y-1.5">
          {checks.map((check, i) => (
            <div key={i} className="flex items-center justify-between gap-2 text-sm">
              <div className="flex items-center gap-2">
                {check.passed ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-success shrink-0" />
                ) : check.required ? (
                  <XCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
                ) : (
                  <AlertTriangle className="h-3.5 w-3.5 text-warning shrink-0" />
                )}
                <span className={cn(!check.passed && check.required && 'text-destructive')}>
                  {check.label}
                </span>
              </div>
              {!check.required && !check.passed && (
                <Badge variant="outline" className="text-[10px] px-1.5 h-4 bg-warning/10 text-warning">
                  Advisory
                </Badge>
              )}
            </div>
          ))}
        </div>

        {/* Blocking reasons */}
        {compliance.blockingReasons.length > 0 && (
          <div className="space-y-1.5 pt-2 border-t border-border">
            <p className="text-xs font-medium text-destructive flex items-center gap-1">
              <Lock className="h-3 w-3" />
              Blocking Issues
            </p>
            {compliance.blockingReasons.map((reason, i) => (
              <p key={i} className="text-xs text-muted-foreground pl-4">
                • {reason}
              </p>
            ))}
          </div>
        )}

        {/* Override notice */}
        {compliance.hasOverrides && (
          <div className="p-2 bg-warning/10 rounded border border-warning/30 text-xs text-warning flex items-center gap-2">
            <AlertTriangle className="h-3 w-3 shrink-0" />
            {compliance.overrideCount} override{compliance.overrideCount > 1 ? 's' : ''} applied — clearly flagged in audit trail
          </div>
        )}

        {/* Disclaimer */}
        <div className="p-2 bg-muted/30 rounded text-[10px] text-muted-foreground leading-relaxed">
          {isDemoMode ? DEMO_COMPLIANCE_NOTICE : COMPLIANCE_DISCLAIMER}
        </div>
      </CardContent>
    </Card>
  );
}
